/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { noop } from 'lodash'
import React, { Fragment, useState } from 'react'
import { defineMessages, useIntl, type IntlShape } from 'react-intl'

import type { DefaultItemAction, EuiBasicTableColumn, EuiSearchBarProps } from '@elastic/eui'
import {
  EuiConfirmModal,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiInMemoryTable,
  EuiText,
  useEuiTheme,
  useGeneratedHtmlId,
} from '@elastic/eui'

import type { CCMService } from '@modules/ccm-user-lib/types'
import ConfirmationModal from '@modules/cui/ConfirmationModal'

import type { FC } from 'react'

export type CCMCluster = {
  id: string
  name?: string
  services: CCMService[]
}

type CCMClusterEventHandlers = {
  onAddOrUpdateNameClicked: (item: CCMCluster) => void
  onDisconnectClicked: (item: CCMCluster) => void
}

type MessageFormatter = IntlShape['formatMessage']

const serviceNameMessages = defineMessages<CCMService>({
  auto_ops: {
    id: 'ccm.clusters-table.service.autoops',
    defaultMessage: 'AutoOps',
  },
  eis: {
    id: 'ccm.clusters-table.service.eis',
    defaultMessage: 'EIS',
  },
})

const messages = defineMessages({
  clusterName: {
    id: 'ccm.clusters-table.table.header.name',
    defaultMessage: 'Cluster',
  },
  services: {
    id: 'ccm.clusters-table.table.header.services',
    defaultMessage: 'Services',
  },
  addDisplayName: {
    id: 'ccm.clusters-table.table.action.add-display-name',
    defaultMessage: 'Add display name',
  },
  updateDisplayName: {
    id: 'ccm.clusters-table.table.action.update-display-name',
    defaultMessage: 'Update display name',
  },
  addDisplayNameDescription: {
    id: 'ccm.clusters-table.table.action.add-display-name.description',
    defaultMessage: 'Add a display name to this cluster',
  },
  disconnectCluster: {
    id: 'ccm.clusters-table.table.action.disconnect-cluster',
    defaultMessage: 'Disconnect cluster',
  },
  disconnectClusterDescription: {
    id: 'ccm.clusters-table.table.action.disconnect-cluster.description',
    defaultMessage: 'Disconnect this cluster from Elastic Cloud Connected Services',
  },
  actions: {
    id: 'ccm.clusters-table.table.header.actions',
    defaultMessage: 'Actions',
  },
  disconnectModalTitle: {
    id: 'ccm.clusters-table.disconnect-cluster.modal.title',
    defaultMessage: 'Disconnect cluster',
  },
  nameToConfirmFormRowLabel: {
    id: 'ccm.clusters-table.disconnect-cluster.modal.name-to-confirm',
    defaultMessage: 'Type the cluster name to confirm',
  },
  modalCancelButton: {
    id: 'ccm.clusters-table.disconnect-cluster.modal.cancel',
    defaultMessage: 'Cancel',
  },
  noClusters: {
    id: 'ccm.clusters-table.table.no-clusters',
    defaultMessage: 'No clusters found',
  },
  searchPlaceholder: {
    id: 'ccm.clusters-table.search.placeholder',
    defaultMessage: 'Search',
  },
  deleteButtonLabel: {
    id: 'ccm.clusters-table.disconnect-cluster.modal.disconnect',
    defaultMessage: 'Disconnect cluster',
  },
  mistypedResourceNameError: {
    id: 'ccm.clusters-table.disconnect-cluster.modal.error',
    defaultMessage: 'The deployment name you typed does not match {name}',
  },
  modalCalloutTitle: {
    id: 'ccm.clusters-table.disconnect-cluster.modal.callout.title',
    defaultMessage: 'Disconnecting a cluster cannot be reversed',
  },
  modalCalloutMessage: {
    id: 'ccm.clusters-table.disconnect-cluster.modal.callout',
    defaultMessage:
      'This action cannot be undone and permanently deletes connection with the cluster {name}. If you ' +
      'proceed, your existing connected services will be disabled and you will lose any data within them.',
  },
  addNameModalTitle: {
    id: 'ccm.clusters-table.add-name-modal.title',
    defaultMessage: 'Add display name for cluster',
  },
  updateNameModalTitle: {
    id: 'ccm.clusters-table.update-name-modal.title',
    defaultMessage: 'Update display name for cluster',
  },
  addNameModalDescription: {
    id: 'ccm.clusters-table.add-name-modal.description',
    defaultMessage:
      'You can provide a human-readable display name for your self-hosted cluster to help you ' +
      'recognize it more easily. This display name will only be visible as a reference to your self-hosted cluster in Cloud.',
  },
  addNameModalFormRowLabel: {
    id: 'ccm.clusters-table.add-name-modal.form-row.label',
    defaultMessage: 'Display name',
  },
  copyToClipboardMessage: {
    id: 'ccm.clusters-table.disconnect-cluster.modal.copy-to-clipboard',
    defaultMessage: 'Copy cluster name to clipboard',
  },
  addDisplayNameConfirm: {
    id: 'ccm.clusters-table.add-display-name-confirm',
    defaultMessage: 'Add',
  },
  updateDisplayNameConfirm: {
    id: 'ccm.clusters-table.update-display-name-confirm',
    defaultMessage: 'Update',
  },
  loadingClusters: {
    id: 'ccm.clusters-table.loading-clusters',
    defaultMessage: 'Loading clusters...',
  },
})

const getTableColumns = (
  formatMessage: MessageFormatter,
  eventHandlers: CCMClusterEventHandlers,
  isCCMAddNameFeatureActivated: boolean,
): Array<EuiBasicTableColumn<CCMCluster>> => [
  {
    field: 'name',
    name: formatMessage(messages.clusterName),
    render: (_, cluster) => <ClusterTableCell cluster={cluster} />,
  },
  {
    field: 'services',
    name: formatMessage(messages.services),
    render: (services: CCMService[]) => (
      <EuiText size='s'>
        {services.map((service) => formatMessage(serviceNameMessages[service])).join(', ')}
      </EuiText>
    ),
  },
  {
    name: formatMessage(messages.actions),
    actions: getRowActions(formatMessage, eventHandlers, isCCMAddNameFeatureActivated),
  },
]

const getRowActions = (
  formatMessage: MessageFormatter,
  { onAddOrUpdateNameClicked, onDisconnectClicked }: CCMClusterEventHandlers,
  isCCMAddNameFeatureActivated: boolean,
): Array<DefaultItemAction<CCMCluster>> => [
  {
    name: ({ name }: CCMCluster) =>
      formatMessage(name ? messages.updateDisplayName : messages.addDisplayName),
    description: formatMessage(messages.addDisplayNameDescription),
    type: 'button',
    onClick: onAddOrUpdateNameClicked,
    available: () => isCCMAddNameFeatureActivated,
  },
  {
    name: formatMessage(messages.disconnectCluster),
    description: formatMessage(messages.disconnectClusterDescription),
    type: 'button',
    onClick: onDisconnectClicked,
  },
]

type ClusterTableProps = {
  clusters: CCMCluster[]
  availableCcmServices: readonly CCMService[]
  onClusterDisconnect: (id: string) => void
  onClusterAddName: (id: string, newName: string) => void
  onClusterUpdateName: (id: string, newName: string) => void
  isCCMAddNameFeatureActivated?: boolean
  isLoading?: boolean
}

const ClusterTable: FC<ClusterTableProps> = ({
  clusters,
  availableCcmServices,
  onClusterAddName = noop,
  onClusterUpdateName = noop,
  onClusterDisconnect = noop,
  isCCMAddNameFeatureActivated = false, // Placeholder for feature flag check
  isLoading = false,
}) => {
  const { formatMessage } = useIntl()

  const [disconnectingCluster, setDisconnectingCluster] = useState<CCMCluster | undefined>(
    undefined,
  )

  const [renamingCluster, setRenamingCluster] = useState<CCMCluster | undefined>(undefined)

  const search: EuiSearchBarProps = {
    box: {
      incremental: true,
      placeholder: formatMessage(messages.searchPlaceholder),
    },
    filters:
      availableCcmServices.length > 1
        ? [
            {
              type: 'field_value_selection',
              field: 'services',
              name: formatMessage(messages.services),
              multiSelect: 'or',
              options: availableCcmServices.map((service) => ({
                value: service,
                view: formatMessage(serviceNameMessages[service]),
              })),
            },
          ]
        : [],
  }

  return (
    <Fragment>
      {renamingCluster && (
        <RenameClusterModal
          cluster={renamingCluster}
          onConfirmAddName={(newName) => {
            onClusterAddName(renamingCluster.id, newName)
            setRenamingCluster(undefined)
          }}
          onConfirmUpdateName={(newName) => {
            onClusterUpdateName(renamingCluster.id, newName)
            setRenamingCluster(undefined)
          }}
          onCancel={() => setRenamingCluster(undefined)}
        />
      )}
      {disconnectingCluster && (
        <DisconnectClusterModal
          disconnectingCluster={disconnectingCluster}
          onConfirm={() => {
            onClusterDisconnect(disconnectingCluster.id)
            setDisconnectingCluster(undefined)
          }}
          onCancel={() => setDisconnectingCluster(undefined)}
        />
      )}
      <EuiInMemoryTable<CCMCluster>
        search={search}
        columns={getTableColumns(
          formatMessage,
          {
            onAddOrUpdateNameClicked: (item) => {
              setRenamingCluster(item)
            },
            onDisconnectClicked: (item) => {
              setDisconnectingCluster(item)
            },
          },
          isCCMAddNameFeatureActivated,
        )}
        items={clusters}
        message={formatMessage(isLoading ? messages.loadingClusters : messages.noClusters)}
        loading={isLoading}
      />
    </Fragment>
  )
}

export const ClusterTableCell: FC<{ cluster: CCMCluster }> = ({ cluster }) => {
  const { euiTheme } = useEuiTheme()

  const { id, name } = cluster

  const textStyleSemiBold = { fontWeight: euiTheme.font.weight.semiBold }

  if (name) {
    return (
      <EuiFlexGroup direction='column' gutterSize='s'>
        <EuiFlexItem>
          <EuiText css={textStyleSemiBold}>{cluster.name}</EuiText>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiText color='subdued'>{cluster.id}</EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  return <EuiText css={textStyleSemiBold}>{id}</EuiText>
}

export const DisconnectClusterModal: FC<{
  disconnectingCluster: CCMCluster
  onConfirm: () => void
  onCancel: () => void
}> = ({ disconnectingCluster, onConfirm, onCancel }) => {
  const { formatMessage } = useIntl()

  const copyToClipboardMessage = formatMessage(messages.copyToClipboardMessage)

  return (
    <ConfirmationModal
      modalTitle={formatMessage(messages.disconnectModalTitle)}
      resourceName={disconnectingCluster.name ?? disconnectingCluster.id}
      onCloseConfirmModal={onCancel}
      onDeleteButtonConfirm={onConfirm}
      nameToConfirmFormRowLabel={formatMessage(messages.nameToConfirmFormRowLabel)}
      copyToClipboardMessage={copyToClipboardMessage}
      cancelButtonLabel={formatMessage(messages.modalCancelButton)}
      deleteButtonLabel={formatMessage(messages.deleteButtonLabel)}
      getMistypedResourceNameErrorMessage={(resourceName: string) =>
        formatMessage(messages.mistypedResourceNameError, { name: resourceName })
      }
      calloutTitle={formatMessage(messages.modalCalloutTitle)}
      getCalloutMessage={(resourceName: React.ReactNode) =>
        formatMessage(messages.modalCalloutMessage, { name: resourceName })
      }
    />
  )
}

export const RenameClusterModal: FC<{
  cluster: CCMCluster
  onConfirmAddName: (clusterName: string) => void
  onConfirmUpdateName: (clusterName: string) => void
  onCancel: () => void
}> = ({ cluster, onConfirmAddName, onConfirmUpdateName, onCancel }) => {
  const modalTitleId = useGeneratedHtmlId()

  const { formatMessage } = useIntl()

  const [newName, setNewName] = useState(cluster.name ?? '')

  return (
    <EuiConfirmModal
      title={formatMessage(
        cluster.name ? messages.updateNameModalTitle : messages.addNameModalTitle,
      )}
      aria-labelledby={modalTitleId}
      titleProps={{
        id: modalTitleId,
      }}
      onCancel={onCancel}
      onConfirm={() => {
        if (cluster.name) {
          onConfirmUpdateName(newName)
        } else {
          onConfirmAddName(newName)
        }
      }}
      cancelButtonText={formatMessage(messages.modalCancelButton)}
      confirmButtonText={formatMessage(
        cluster.name ? messages.updateDisplayNameConfirm : messages.addDisplayNameConfirm,
      )}
      defaultFocusedButton='confirm'
      buttonColor='primary'
    >
      <EuiFormRow label={formatMessage(messages.addNameModalFormRowLabel)} fullWidth={true}>
        <EuiFieldText
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          fullWidth={true}
        />
      </EuiFormRow>
    </EuiConfirmModal>
  )
}

export default ClusterTable
